import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Minus, Trash2, Lock, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';
import { formatCurrency } from '@/utils/currency';

const LOCKED_STAGES = ['PREPARED', 'READY'];

// Post Pay-specific item editing — the walk-up-counter equivalent of a
// customer changing their mind, since there's no waiter to catch it
// before the kitchen. Fundamentally different from the regular
// EditOrderModal: this never replaces the whole item list, it only ever
// adds new items or flags existing ones as removed — a locked item
// (kitchen already finished it) can never be pulled, while everything
// else on the same order stays freely editable. A removed item never
// actually disappears from the list here, only strikes through, so
// staff can always see the real edit history.
const PostPayEditModal = ({ order, open, onClose, onSuccess }) => {
  const [menu, setMenu] = useState([]);
  const [items, setItems] = useState([]);
  const [pendingRemovals, setPendingRemovals] = useState(new Set());
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (open && order) {
      setItems(order.items || []);
      setPendingRemovals(new Set());
      setPendingAdditions([]);
      loadMenu();
    }
  }, [open, order]);

  const loadMenu = async () => {
    // Reads from the same modern catalog every other screen uses tonight
    // (catalog_products) - the old /menu endpoint pointed at the stale
    // menu_items collection, which is why whole categories like
    // Desserts, Cold Beverages, and Fries were missing here even though
    // they exist correctly everywhere else.
    try {
      const res = await apiClient.get(`/catalog/${order.store_id}/menu`, { params: { channel: 'pos_walkin' } });
      const flatItems = [];
      (res.data.categories || []).forEach((cat) => {
        (cat.products || []).forEach((p) => {
          if (!p.active) return;
          const variants = p.variants && p.variants.length > 0 ? p.variants : [{ id: 'default', name: 'Regular', price: 0, is_default: true }];
          const defaultVariant = variants.find((v) => v.is_default) || variants[0];
          flatItems.push({
            id: p.id,
            name: variants.length > 1 ? `${p.name} (${defaultVariant.name})` : p.name,
            price: (defaultVariant.price || 0) / 100,
            category: cat.name,
          });
        });
      });
      setMenu(flatItems);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const toggleRemoval = (idx, item) => {
    if (LOCKED_STAGES.includes(item.prep_status)) {
      toast.error(`Can't remove ${item.menu_item_name} — kitchen has already prepared it`);
      return;
    }
    setPendingRemovals((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const addMenuItem = (menuItem) => {
    setPendingAdditions((prev) => [
      ...prev,
      {
        menu_item_id: menuItem.id,
        menu_item_name: menuItem.name,
        qty: 1,
        price: menuItem.price,
        notes: '',
      },
    ]);
  };

  const adjustNewItemQty = (idx, delta) => {
    setPendingAdditions((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, qty: Math.max(1, it.qty + delta) } : it))
    );
  };

  const removeNewItem = (idx) => {
    setPendingAdditions((prev) => prev.filter((_, i) => i !== idx));
  };

  const runningTotal = () => {
    const keptExisting = items
      .filter((_, idx) => !pendingRemovals.has(idx))
      .reduce((sum, it) => sum + it.price * it.qty, 0);
    const added = pendingAdditions.reduce((sum, it) => sum + it.price * it.qty, 0);
    return keptExisting + added;
  };

  const handleSave = async () => {
    if (pendingRemovals.size === 0 && pendingAdditions.length === 0) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.put(`/orders/${order.id}/postpay-items`, {
        remove_indices: Array.from(pendingRemovals),
        add_items: pendingAdditions,
      });
      toast.success('Order updated — a fresh ticket has been sent to the kitchen');
      onSuccess(res.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  const categories = [...new Set(menu.map((m) => m.category))].sort();
  const filteredMenu = menu.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()) &&
    (!selectedCategory || m.category === selectedCategory)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Order #{order.order_number} (Post Pay)</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">In this order</p>
            <div className="space-y-1.5">
              {items.map((item, idx) => {
                const locked = LOCKED_STAGES.includes(item.prep_status);
                const marked = pendingRemovals.has(idx);
                const alreadyRemoved = item.edit_state === 'REMOVED';
                if (alreadyRemoved) {
                  return (
                    <div key={idx} className="flex items-center justify-between px-2.5 py-1.5 rounded bg-gray-50 opacity-60">
                      <span className="text-xs line-through text-gray-400 truncate">
                        {item.qty}x {item.menu_item_name}
                      </span>
                      <span className="text-xs text-gray-400 shrink-0 ml-2">Removed earlier</span>
                    </div>
                  );
                }
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded ${marked ? 'bg-red-50' : 'bg-gray-50'}`}
                  >
                    <div className={`min-w-0 flex-1 ${marked ? 'line-through text-gray-400' : ''}`}>
                      <span className="text-sm font-medium truncate">{item.qty}x {item.menu_item_name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-sm font-medium">{formatCurrency(item.price * item.qty)}</span>
                      {locked ? (
                        <span className="flex items-center gap-1 text-xs text-gray-400" data-testid="postpay-item-locked">
                          <Lock className="h-3 w-3" /> {item.prep_status === 'READY' ? 'Ready' : 'Prepared'}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleRemoval(idx, item)}
                          data-testid="postpay-remove-toggle"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {pendingAdditions.map((item, idx) => (
                <div key={`new-${idx}`} className="flex items-center justify-between px-2.5 py-1.5 rounded bg-green-50">
                  <div className="min-w-0 flex-1 flex items-center gap-1.5">
                    <Plus className="h-3 w-3 text-green-600 shrink-0" />
                    <span className="text-sm font-medium text-green-800 truncate">{item.qty}x {item.menu_item_name}</span>
                    <span className="text-xs text-green-600 shrink-0">new</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => adjustNewItemQty(idx, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="text-sm w-4 text-center">{item.qty}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => adjustNewItemQty(idx, 1)}><Plus className="h-3 w-3" /></Button>
                    <span className="text-sm font-medium text-green-800 w-14 text-right">{formatCurrency(item.price * item.qty)}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeNewItem(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Input placeholder="Search menu to add an item…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2 h-9 text-sm" />
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${!selectedCategory ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'}`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${selectedCategory === cat ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto pr-1">
              {filteredMenu.map((m) => (
                <button
                  key={m.id}
                  onClick={() => addMenuItem(m)}
                  className="min-w-0 flex items-center gap-2 p-1.5 rounded border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-left"
                >
                  <span className="h-7 w-7 shrink-0 rounded bg-gray-100 flex items-center justify-center">
                    <UtensilsCrossed className="h-3.5 w-3.5 text-gray-400" />
                  </span>
                  <span className="text-sm font-medium truncate flex-1 min-w-0">{m.name}</span>
                  <span className="text-sm text-gray-500 shrink-0">{formatCurrency(m.price)}</span>
                </button>
              ))}
              {filteredMenu.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No items match</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              <p className="text-xs text-gray-500">New total</p>
              <p className="text-lg font-semibold text-green-600">{formatCurrency(runningTotal())}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="postpay-save-btn">
            {saving ? 'Saving…' : 'Save & Send to Kitchen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostPayEditModal;
